<template>
  <div class="pdf-edit-page">
    <!-- Loading -->
    <template v-if="loading">
      <TopBarTitleComponent title="Cargando..." :showBackIcon="true" />
      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <SkeletonLoadingCard />
      </b-container>
    </template>

    <!-- Not editable (wrong status/source) -->
    <template v-else-if="!canEdit">
      <TopBarTitleComponent title="Editar PDF" :showBackIcon="true" />
      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <EmptyPage
          text="Este documento ya no se puede editar (solo se pueden editar PDFs cargados que sigan en borrador)."
        />
      </b-container>
    </template>

    <template v-else>
      <TopBarTitleComponent :title="document.name" :showBackIcon="true">
        <template slot="right-area">
          <div class="d-flex align-items-center" style="gap:10px">
            <span class="pdf-edit-page__autosave" v-if="autosaving">
              <i class="el-icon-loading"></i> Guardando…
            </span>
            <button
              class="btn md__btn-third-secondary"
              :disabled="applying"
              @click="applyFields"
            >
              <i class="el-icon-loading" v-if="applying" />
              <i class="el-icon-check" v-else />
              Guardar
            </button>
            <button
              class="btn md__btn-primary"
              v-if="applied"
              @click="goToSign"
            >
              <i class="el-icon-edit-outline"></i> Firmar documento
            </button>
          </div>
        </template>
      </TopBarTitleComponent>

      <div class="pdf-edit-page__body">
        <PdfFieldEditor
          ref="editor"
          :pdf-data="pdfArrayBuffer"
          :initial-fields="initialFields"
          @fields-change="onFieldsChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";
import SkeletonLoadingCard from "@/components/Animations/Loading/SkeletonCard";
import EmptyPage from "../../components/EmptyPage";
import PdfFieldEditor from "../components/PdfFieldEditor.vue";
import documentoService from "../services/documentoService";

export default {
  name: "DocumentPdfEditPage",
  components: {
    TopBarTitleComponent,
    SkeletonLoadingCard,
    EmptyPage,
    PdfFieldEditor
  },
  data() {
    return {
      loading: true,
      document: null,
      pdfArrayBuffer: null,
      initialFields: [],
      autosaveTimer: null,
      // Chains every save request one after another so a slower, older
      // request can never resolve after a newer one and overwrite it with
      // stale/incomplete text_fields (that's how fields used to go missing
      // or end up at an outdated position when saving quickly).
      saveChain: Promise.resolve(),
      autosaving: false,
      applying: false,
      applied: false
    };
  },
  computed: {
    documentId() {
      return this.$route.params.id;
    },
    canEdit() {
      return (
        !!this.document &&
        this.document.status === "draft" &&
        this.document.source === "upload"
      );
    }
  },
  methods: {
    async loadDocument() {
      this.loading = true;
      try {
        const metaRes = await documentoService.getDocument(this.documentId);
        const doc = metaRes && metaRes.data ? metaRes.data.data : null;
        if (!doc) {
          this.document = null;
          return;
        }
        this.document = doc;
        this.initialFields = doc.text_fields || [];

        if (doc.status === "draft" && doc.source === "upload") {
          // Always render the pristine original here, never file_path —
          // file_path may already have fields baked in from a previous
          // "Guardar", and overlaying the same editable fields on top of
          // that would show every value twice.
          const pdfRes = await documentoService.previewPdf(this.documentId, {
            params: { original: 1 },
            skipErrorNotification: true
          });
          if (pdfRes && pdfRes.data) {
            this.pdfArrayBuffer = new Uint8Array(pdfRes.data);
          }
        }
      } catch (e) {
        this.document = null;
        this.$notifications.error({
          message: "No se pudo cargar el documento."
        });
      } finally {
        this.loading = false;
      }
    },

    onFieldsChange(fields) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = setTimeout(() => {
        this.queueSaveFields(fields);
      }, 800);
    },

    // Queues a save behind any request already in flight, instead of firing
    // it in parallel — two concurrent PUTs can resolve out of order, and
    // whichever lands last on the server wins even if it was the older,
    // less complete one.
    queueSaveFields(fields) {
      this.autosaving = true;
      this.saveChain = this.saveChain
        .catch(() => {})
        .then(() =>
          documentoService.updateDocument(this.documentId, {
            text_fields: fields
          })
        )
        .catch(() => {
          this.$notifications.error({
            message: "No se pudieron guardar los cambios de los campos."
          });
        })
        .finally(() => {
          this.autosaving = false;
        });
      return this.saveChain;
    },

    async applyFields() {
      // Make sure the latest field positions/values are saved before baking
      // them — queued behind any autosave already in flight so it's always
      // the last (and therefore winning) write.
      clearTimeout(this.autosaveTimer);
      const fields = this.$refs.editor ? this.$refs.editor.getFields() : [];

      this.applying = true;
      try {
        await this.queueSaveFields(fields);

        if (!fields.length) {
          this.$notifications.warning({
            message: "Agrega al menos un campo antes de guardar."
          });
          return;
        }

        await documentoService.applyTextFields(this.documentId);
        this.$notifications.success({
          message: "Campos aplicados al documento correctamente."
        });

        // Don't refresh the preview from the now-baked file_path here — the
        // editable field overlay is still showing on top of it, which would
        // display every value twice. The baked result shows up cleanly
        // (with no overlay) once the user continues to the sign flow.
        this.applied = true;
      } catch (e) {
        this.$notifications.error({
          message: "Error al aplicar los campos al documento."
        });
      } finally {
        this.applying = false;
      }
    },

    goToSign() {
      this.$router.push({
        name: "documentos.sign-flow",
        query: { documentId: this.documentId }
      });
    }
  },
  created() {
    this.loadDocument();
  },
  beforeDestroy() {
    clearTimeout(this.autosaveTimer);
  }
};
</script>

<style lang="scss" scoped>
.pdf-edit-page__body {
  height: calc(100vh - 64px);
  padding: 16px;
  box-sizing: border-box;
}

.pdf-edit-page__autosave {
  font-size: 12px;
  color: #989aae;
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
